/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./MeetDetailKendoReportValidate";
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import useTimeStandardTypeData from '@usas/ui-web/state/timeStandardType/UseTimeStandardTypeData';
import Constants from '@usas/ui-web/utils/Constants';

import useOrgUnitData from '../../state/orgUnit/UseOrgUnitData';


// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { width: "290px", id: "MeetName", field: "meetName", title: "Meet Name" },
  { width: "140px", id: "MeetClassification", field: "meetClassification", title: "Meet Classification" },
  { width: "120px", id: "MeetType", field: "meetType", title: "Meet Type" },
  { width: "110px", id: "StartDate", field: "startDate", title: "Start Date", format: "{0:MM/dd/yyyy}" },
  { width: "110px", id: "EndDate", field: "endDate", title: "End Date", format: "{0:MM/dd/yyyy}" },
  { width: "90px", id: "LscCode", field: "lscCode", title: "LSC" },
  { width: "95px", id: "ClubCode", field: "clubCode", title: "Host Code" },
  { width: "205px", id: "ClubName", field: "clubName", title: "Host Name" },
  { width: "90px", id: "Course", field: "course", title: "Course" },
  { width: "90px", id: "TotalLSCs", field: "totalLSCs", title: "LSCs" },
  { width: "90px", id: "TotalClubs", field: "totalClubs", title: "Clubs" },
  { width: "90px", id: "Splashes", field: "splashes", title: "Splashes" },
  { width: "110px", id: "DistinctEvents", field: "distinctEvents", title: "Distinct Events" },
  { width: "90px", id: "Swimmers", field: "swimmers", title: "Swimmers" },
  { width: "140px", id: "LifetimeBestTimes", field: "lifetimeBestTimes", title: "Lifetime Best Times" },
  { width: "140px", id: "SeasonBestTimes", field: "seasonBestTimes", title: "Season Best Times" }  
];

const excelColumns = [
  { id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { id: "MeetName", field: "meetName", title: "Meet Name" },
  { id: "MeetClassification", field: "meetClassification", title: "Meet Classification" },
  { id: "MeetType", field: "meetType", title: "Meet Type" },
  { id: "StartDate", field: "startDate", title: "Start Date" },
  { id: "EndDate", field: "endDate", title: "End Date" },
  { id: "LscCode", field: "lscCode", title: "LSC" },
  { id: "ClubCode", field: "clubCode", title: "Host Code" },
  { id: "ClubName", field: "clubName", title: "Host Name" },
  { id: "Course", field: "course", title: "Course" },
  { id: "TotalLSCs", field: "totalLSCs", title: "LSCs" },
  { id: "TotalClubs", field: "totalClubs", title: "Clubs" },
  { id: "Splashes", field: "splashes", title: "Splashes" },
  { id: "DistinctEvents", field: "distinctEvents", title: "Distinct Events" },
  { id: "Swimmers", field: "swimmers", title: "Swimmers" },
  { id: "LifetimeBestTimes", field: "lifetimeBestTimes", title: "Lifetime Best Times" },
  { id: "SeasonBestTimes", field: "seasonBestTimes", title: "Season Best Times" }  
];

const REPORT_INFO_ID = 286;
  
const useMeetDetailKendoReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [seasonYearState, setSeasonYearState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const { timeStandardTypeState, getTimeStandardTypesByOrgUnitId } = useTimeStandardTypeData();
  const [filteredTimeStandardTypeState, setFilteredTimeStandardTypeState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [modalState, setModalState] = useState(getInitialModalState());
  const { orgUnitState } = useOrgUnitData(); 
  
  const {
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      courses: [],
      startDate: Constants.BLANK_DATE_STRING,
      endDate: Constants.BLANK_DATE_STRING,
      seasonId: -1,
      seasonYear: -1,
      clubs: [],
      lscs: [],
      zones: [],
      meetClassifications: [],
      meetTypes: [],
      meetsNoTime: false
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }

    if (timeStandardTypeState.isArrayLoading === false && timeStandardTypeState.isArrayLoaded === false) {
      getTimeStandardTypesByOrgUnitId(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  useEffect(() => {
    if (timeStandardTypeState.isArrayLoaded === true) {
      const standardTypes = [];
      timeStandardTypeState.arrayData.map(st => {
        if (st.assignToTimes === true && st.availableToHq === true) {
          standardTypes.push({ id: st.sortOrder, name: st.typeName });
        }
      });
      setFilteredTimeStandardTypeState({ isLoading: false, isLoaded: true, options: standardTypes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStandardTypeState]);

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    reportParameters: {
      OrgUnitId_Int: formState.orgUnitId === "" ? null : formState.orgUnitId,
      Courses: formState.courses.map(course => course.id).join(',') || null, 
      StartDate: formState.startDate === Constants.BLANK_DATE_STRING ? null : formState.startDate, 
      EndDate: formState.endDate === Constants.BLANK_DATE_STRING ? null : formState.endDate, 
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10), 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','), 
      MeetClassifications: formState.meetClassifications.map(meetClassification => meetClassification.name).join(',') || "",
      MeetTypes: formState.meetTypes.map(meetType => meetType.name).join(',') || null,
      MeetsNoTime_Int: formState.meetsNoTime === true ? 1 : 0 
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    filteredTimeStandardTypeState,
    excelColumns,
    clubFilterIds: getClubFileterIds(),
    REPORT_INFO_ID: REPORT_INFO_ID
  };
};

export default useMeetDetailKendoReport;